/*
  Newsletter — full-bleed editorial carousel.

  Rows are independent
  --------------------
  Each row owns its tiles, its tile count and its own position. A feature
  tile is NOT tied to a small tile: the merchant adds Feature tile blocks
  and Small tile blocks separately and there can be any number of each.
  One step advances every row by one of ITS OWN tiles, so the rows read as
  two different speeds (the small row's tiles are narrower, so it covers
  less ground per step) while staying one gesture.

  Each row writes --wt-nl-pos on its own track; the CSS centres that row's
  tile width against the viewport, so nothing here measures anything and a
  resize re-centres on its own.

  Seamless looping
  ----------------
  Every row renders three copies of its tiles: [clones][real][clones], and
  its position lives in the middle copy. When a move carries it out, the
  class `is-jumping` kills that row's transition for one frame and shifts
  its position by exactly its own count. The tile under the centre is
  identical before and after, so the jump is invisible — and because each
  row wraps against its own count, rows of different lengths never fall
  out of step.

  The autoplay clock is CSS
  -------------------------
  The active dot's gold fill runs a CSS animation of the merchant's
  interval and its `animationend` is what advances the carousel, so the
  progress bar cannot drift from the tile and "paused" is a single state
  rather than a timer and a bar that can disagree. Pausing happens for
  three reasons — the visitor pressed pause, the section scrolled out of
  view, or the tab went to the background — combined into one boolean
  before it reaches the DOM.

  The dots belong to the feature row: one dot per story is the merchant's
  mental model. With fewer than two feature tiles there is nothing to page
  through, so the section renders static (Liquid drops the controls).
*/

class NewsletterCarousel {
  constructor(root) {
    this.root = root;
    this.dotsWrap = root.querySelector('[data-newsletter-dots]');
    this.dots = Array.from(root.querySelectorAll('[data-newsletter-dot]'));
    this.toggle = root.querySelector('[data-newsletter-toggle]');

    this.rows = Array.from(root.querySelectorAll('[data-newsletter-row]')).map((el) => {
      const count = parseInt(el.dataset.count, 10) || 0;
      return {
        el,
        track: el.querySelector('[data-newsletter-track]'),
        count,
        // A row with one tile has nothing to advance to; it stays put
        // while the other row moves.
        fixed: count < 2,
        pos: count < 2 ? 0 : count,
      };
    });

    this.clock = this.rows[0]; // the feature row drives the dots
    if (!this.clock) return;

    this.rows.forEach((row) => {
      if (!row.fixed) this.cloneSet(row.track);
    });
    this.fit();

    // Tiles are sized from the row height, which depends on the viewport, so
    // re-fit on every resize (the static-section return below must not skip it).
    this.onFitResize = () => {
      cancelAnimationFrame(this.fitFrame);
      this.fitFrame = requestAnimationFrame(() => this.fit());
    };
    window.addEventListener('resize', this.onFitResize);

    // Bound before the static-section return: phones tap-to-reveal either way.
    this.bindTap();

    // Same reason: a one-tile section still has a caption to trim.
    this.bindCaptions();

    if (this.clock.fixed) return; // static section: no dots, no autoplay

    this.userPaused = root.dataset.autoplay === 'false';
    this.inView = true;

    this.renderDots(true);
    this.bind();
    this.applyPaused();
  }

  /* ---------- setup ---------- */

  // [clones][real][clones]. Clones are decorative duplicates: hidden from
  // assistive tech and taken out of the tab order, so a keyboard user
  // never lands on the same tile three times.
  cloneSet(track) {
    const originals = Array.from(track.children);

    const copy = (node) => {
      const clone = node.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.clone = 'true';
      // The theme editor finds blocks by these attributes; three elements
      // answering to one block id makes its outline and block-select land
      // on an arbitrary copy. Only the real tile keeps them.
      clone.removeAttribute('data-shopify-editor-block');
      clone.removeAttribute('id');
      clone.querySelectorAll('a, button').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      return clone;
    };

    const before = originals.map(copy);
    const after = originals.map(copy);

    before.reverse().forEach((clone) => track.insertBefore(clone, track.firstChild));
    after.forEach((clone) => track.appendChild(clone));
  }

  bind() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToIndex(index));
    });

    if (this.toggle) {
      this.toggle.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.applyPaused();
      });
    }

    // Autoplay tick: the active dot's fill finished, so the tile is done.
    if (this.dotsWrap) {
      this.dotsWrap.addEventListener('animationend', (event) => {
        if (event.animationName === 'wt-nl-fill') this.step(1);
      });
    }

    // Each row wraps on its own count, so each listens for its own move
    // to finish — a shared listener would reset a row mid-animation.
    this.rows.forEach((row) => {
      if (row.fixed) return;
      row.track.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'transform' && event.target === row.track) this.normalise(row);
      });
    });

    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1, true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1, true);
      }
    });

    this.rows.forEach((row) => this.bindDrag(row));

    document.addEventListener('visibilitychange', () => this.applyPaused());

    // Tile widths are vw-based, so every resize step re-targets the
    // transforms and the tracks would ease toward them — a rotating phone
    // or a dragged browser edge would show the carousel sliding around.
    // Hold the transitions off while the size is moving.
    this.onResize = () => {
      this.rows.forEach((row) => row.track.classList.add('is-jumping'));
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.rows.forEach((row) => row.track.classList.remove('is-jumping'));
      }, 150);
    };
    window.addEventListener('resize', this.onResize);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          this.inView = entries[0].isIntersecting;
          this.applyPaused();
        },
        { threshold: 0.25 }
      );
      this.observer.observe(this.root);
    }
  }

  /* ---------- phone tap-to-reveal ----------
     Phones have no hover, so a tile's first tap slides its panel up instead
     of following the link; the button on the open panel is what navigates.
     Tapping the open tile's art again closes it, as does tapping another
     tile or anywhere off the carousel. Autoplay holds while a panel is open
     so the tile being read never slides away. Wider screens are untouched:
     the whole card stays a link there. */
  bindTap() {
    this.phoneQuery = window.matchMedia('(max-width: 749px)');

    // Returns false when the tap should be left alone to follow the link
    // (the open panel's button, or a tile with no button at all).
    const toggle = (card, target) => {
      const slide = card.closest('.wt-newsletter__slide');
      const isOpen = slide.classList.contains('is-open');
      const cta = card.querySelector('.wt-newsletter__cta');

      if (isOpen && (target.closest('.wt-newsletter__cta') || !cta)) return false;

      this.closeTiles();
      if (!isOpen) {
        slide.classList.add('is-open');
        this.tileOpen = true;
        this.applyPaused();
      }
      return true;
    };

    /* Touch taps are acted on at pointerup, not click. Android Chrome sends
       no click at all for a tap made within about a second of a swipe on
       the row (seen on a Galaxy A52 with the ?vdebug log: pointerdown and
       pointerup arrive, click never does), so a tile tapped straight after
       swiping to it needed a second tap. The click that normally follows is
       then only cancelled, so the card's link is not followed and the tile
       is not toggled twice. */
    let down = null;
    let consumedAt = -Infinity;

    this.root.addEventListener('pointerdown', (event) => {
      down = null;
      if (event.pointerType === 'mouse' || !this.phoneQuery.matches) return;
      const card = event.target.closest('.wt-newsletter__card');
      if (!card || !this.root.contains(card)) return;
      down = { id: event.pointerId, x: event.clientX, y: event.clientY, card };
    });

    this.root.addEventListener('pointerup', (event) => {
      const start = down;
      down = null;
      if (!start || event.pointerId !== start.id) return;
      // Past the drag threshold this was a swipe, which bindDrag owns.
      if (Math.abs(event.clientX - start.x) > 8 || Math.abs(event.clientY - start.y) > 8) return;
      // Judge the tap by where the finger came down: the row may still be
      // easing into place underneath it.
      const target = event.target.closest('.wt-newsletter__card') === start.card ? event.target : start.card;
      if (toggle(start.card, target)) consumedAt = performance.now();
    });

    this.root.addEventListener('pointercancel', () => {
      down = null;
    });

    this.root.addEventListener('click', (event) => {
      const card = event.target.closest('.wt-newsletter__card');
      if (!this.phoneQuery.matches) return;
      if (!card || !this.root.contains(card)) return;
      // Keyboard activation (Enter) reports detail 0: focus already shows
      // the panel, so let it follow the link.
      if (event.detail === 0) return;

      // The pointerup above already handled this tap.
      if (performance.now() - consumedAt < 700) {
        consumedAt = -Infinity;
        event.preventDefault();
        return;
      }

      if (toggle(card, event.target)) event.preventDefault();
    });

    this.onOutsideTap = (event) => {
      if (this.tileOpen && !this.root.contains(event.target)) this.closeTiles();
    };
    document.addEventListener('click', this.onOutsideTap);

    this.phoneQuery.addEventListener('change', () => this.closeTiles());
  }

  closeTiles() {
    if (!this.tileOpen) return;
    this.root.querySelectorAll('.wt-newsletter__slide.is-open').forEach((slide) => {
      slide.classList.remove('is-open');
    });
    this.tileOpen = false;
    this.applyPaused();
  }

  /* ---------- one-line captions (desktop) ----------
     Client, 2026-09-22: every tile shows exactly ONE line of caption, so
     the row reads as a row instead of blocks of text at three different
     depths. A caption that fits its line is left alone; a longer one is
     cut at the line and given a gold "…" that opens the whole text.

     Measured, not counted. A character budget would land in a different
     place on every tile width and in every language — the section ships
     English and Arabic, which do not fit the same number of characters in
     the same space. The span is asked how wide it wants to be and words
     come off until it fits what it has.

     Desktop only. Between 750 and 989px the caption keeps its two-line
     clamp, and below 750px the stylesheet hides it: a phone tile has no
     room for a caption beside the button. */
  bindCaptions() {
    this.captions = Array.from(this.root.querySelectorAll('[data-newsletter-caption]'))
      .filter((span) => !span.closest('[data-clone]')) // skip loop clones
      .map((span) => ({
        span,
        line: span.parentElement,
        full: span.textContent.trim(),
        card: span.closest('.wt-newsletter__card'),
      }));

    this.dialog = this.root.querySelector('[data-newsletter-dialog]');
    if (!this.captions.length || !this.dialog) return;

    this.desktopQuery = window.matchMedia('(min-width: 990px)');

    this.onCaptionResize = () => {
      cancelAnimationFrame(this.captionFrame);
      this.captionFrame = requestAnimationFrame(() => this.trimCaptions());
    };
    window.addEventListener('resize', this.onCaptionResize);
    this.desktopQuery.addEventListener('change', () => this.trimCaptions());

    // Web fonts land after first paint and change every measurement, so
    // re-measure once they are in. Without this the trim is made against
    // the fallback font and lands a word or two short.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.trimCaptions());
    }

    this.bindDialog();
    this.trimCaptions();

    // Clones are built before this runs, so the trimmed markup is copied
    // into them; re-cloning is not needed. The clones re-trim with the
    // originals because they carry the same data attribute — except they
    // are aria-hidden, which the filter above drops. Give them the
    // original's finished markup instead, on every pass.
    this.syncClones();
  }

  trimCaptions() {
    const desktop = !this.desktopQuery || this.desktopQuery.matches;

    this.captions.forEach((entry) => {
      const { span, line, full } = entry;

      // Always start from the whole string: a resize can hand a caption
      // MORE room than the last pass left it.
      span.textContent = full;
      const existing = line.querySelector('.wt-newsletter__caption-more');
      if (existing) existing.remove();

      if (!desktop) return;
      if (span.scrollWidth <= span.clientWidth) return; // fits: no dots

      const more = document.createElement('span');
      more.className = 'wt-newsletter__caption-more';
      more.textContent = '…';
      more.setAttribute('role', 'button');
      more.setAttribute('tabindex', '0');
      more.setAttribute('aria-label', this.dialog.dataset.moreLabel || 'Read the rest');
      more.dataset.newsletterMore = '';
      line.appendChild(more);

      // The dots are now a flex sibling, so the span's own clientWidth is
      // already what is left for the text — nothing to subtract by hand.
      const words = full.split(/\s+/);
      let lo = 0;
      let hi = words.length;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        span.textContent = words.slice(0, mid).join(' ');
        if (span.scrollWidth <= span.clientWidth) lo = mid;
        else hi = mid - 1;
      }

      // Never leave the line empty — one word that overflows still reads
      // better than dots on their own.
      span.textContent = words.slice(0, Math.max(lo, 1)).join(' ').replace(/[\s,;:—-]+$/, '');
    });

    this.syncClones();
  }

  // The loop renders [clones][real][clones]; a clone was copied before the
  // trim ran, so it would show the untrimmed caption as it scrolled past.
  // Each clone carries its source's block index, so copy the finished line
  // across by position within its row.
  syncClones() {
    this.root.querySelectorAll('[data-newsletter-row]').forEach((row) => {
      const slides = Array.from(row.querySelectorAll('[data-newsletter-slide]'));
      const sources = slides.filter((slide) => !slide.dataset.clone);
      if (!sources.length) return;

      slides.forEach((slide) => {
        if (!slide.dataset.clone) return;
        const index = parseInt(slide.dataset.blockIndex, 10);
        const source = sources[index];
        if (!source) return;
        const from = source.querySelector('.wt-newsletter__caption');
        const to = slide.querySelector('.wt-newsletter__caption');
        if (!from || !to) return;
        to.innerHTML = from.innerHTML;
        // Same rule as the anchors and buttons in cloneSet: a clone is
        // decorative, so its dots must not collect a tab stop of their own.
        to.querySelectorAll('[data-newsletter-more]').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
      });
    });
  }

  /* ---------- the rest of the caption ----------
     A native <dialog>: showModal() brings Esc, the backdrop, the focus
     trap and the return of focus to the "…" with it. The listener is on
     the section root rather than on each "…" because the dots are created
     and destroyed on every resize pass. */
  bindDialog() {
    const open = (more) => {
      const card = more.closest('.wt-newsletter__card');
      const entry = this.captions.find((c) => c.line.contains(more)) ||
        this.captions.find((c) => c.card && card && c.card.dataset.blockIndex === card.dataset.blockIndex);
      const slide = more.closest('.wt-newsletter__slide');
      const title = slide && slide.querySelector('.wt-newsletter__title');
      const deck = slide && slide.querySelector('.wt-newsletter__deck');
      const cta = slide && slide.querySelector('.wt-newsletter__cta');

      const body = this.dialog.querySelector('[data-newsletter-dialog-body]');
      const heading = this.dialog.querySelector('[data-newsletter-dialog-title]');
      const link = this.dialog.querySelector('[data-newsletter-dialog-cta]');

      // A clone's own caption is a copy, so fall back to reading the text
      // out of the line that was clicked.
      body.textContent = entry ? entry.full : more.parentElement.textContent.replace(/…\s*$/, '').trim();

      if (heading && title) {
        heading.textContent = [title.textContent, deck && deck.textContent]
          .filter(Boolean)
          .map((s) => s.trim())
          .join(': ');
      }

      if (link) {
        const href = card && card.getAttribute('href');
        link.textContent = cta ? cta.textContent.trim() : '';
        if (href) {
          link.href = href;
          link.hidden = false;
        } else {
          link.hidden = true;
        }
      }

      this.dialogOpen = true;
      this.applyPaused();
      this.dialog.showModal();
    };

    this.root.addEventListener('click', (event) => {
      const more = event.target.closest('[data-newsletter-more]');
      if (!more) return;
      // The whole tile is a link to the article; without this the click
      // navigates out from under the dialog.
      event.preventDefault();
      event.stopPropagation();
      open(more);
    });

    this.root.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const more = event.target.closest('[data-newsletter-more]');
      if (!more) return;
      event.preventDefault();
      event.stopPropagation();
      open(more);
    });

    const close = this.dialog.querySelector('[data-newsletter-dialog-close]');
    if (close) close.addEventListener('click', () => this.dialog.close());

    // Click outside the panel. <dialog> reports a backdrop click as a
    // click on the dialog itself, so compare against the inner panel.
    this.dialog.addEventListener('click', (event) => {
      if (!event.target.closest('.wt-newsletter__dialog-inner')) this.dialog.close();
    });

    this.dialog.addEventListener('close', () => {
      this.dialogOpen = false;
      this.applyPaused();
    });
  }

  /* ---------- pointer drag ---------- */

  bindDrag(row) {
    let startX = 0;
    let startY = 0;
    let dx = 0;
    let active = false;
    let decided = false;
    let pointerId = null;

    // Measured on the row being dragged, so a swipe across the small tiles
    // is judged against small tiles.
    const stepPx = () => {
      const tile = row.track.children[row.pos] || row.track.querySelector('[data-newsletter-slide]');
      if (!tile) return 1;
      const gap = parseFloat(getComputedStyle(row.track).columnGap) || 0;
      return tile.getBoundingClientRect().width + gap;
    };

    // Swallow the click that ends a drag, so a swipe across a tile never
    // navigates to its link. The window is short on purpose: a mouse drag
    // is followed by its click at once, but a touch swipe (or a page scroll
    // that began on the row and was cancelled) produces no click at all,
    // and a one-shot listener left armed there ate the visitor's NEXT tap —
    // the tile then needed two taps to open.
    let swallowUntil = 0;
    row.el.addEventListener(
      'click',
      (e) => {
        if (performance.now() > swallowUntil) return;
        swallowUntil = 0;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );

    const end = (event) => {
      if (!active) return;
      active = false;
      this.root.classList.remove('is-dragging');
      this.root.style.setProperty('--wt-nl-drag', '0px');

      if (decided) {
        // Round to the nearest tile, but never let one flick throw the
        // carousel more than two stories — that reads as a glitch.
        const moved = Math.max(-2, Math.min(2, Math.round(-dx / stepPx())));
        this.step(moved || 0, true);
        // A cancelled pointer never clicks, so there is nothing to swallow.
        if (Math.abs(dx) > 6 && event.type === 'pointerup') {
          swallowUntil = performance.now() + 300;
        }
      }

      decided = false;
      dx = 0;
      pointerId = null;
    };

    row.el.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      active = true;
      decided = false;
      startX = event.clientX;
      startY = event.clientY;
      pointerId = event.pointerId;
    });

    row.el.addEventListener('pointermove', (event) => {
      if (!active || event.pointerId !== pointerId) return;
      dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!decided) {
        // Let the browser keep vertical scrolling: only claim the gesture
        // once it is clearly horizontal.
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) {
          active = false;
          return;
        }
        if (Math.abs(dx) < 8) return;
        decided = true;
        this.root.classList.add('is-dragging');
        // Throws if the pointer was released between this move and the
        // capture. The drag still works from the row's own listeners, so
        // it is not worth failing over.
        try {
          row.el.setPointerCapture(pointerId);
        } catch (e) {
          /* no-op */
        }
      }

      this.root.style.setProperty('--wt-nl-drag', `${dx}px`);
    });

    row.el.addEventListener('pointerup', end);
    row.el.addEventListener('pointercancel', end);
    row.el.addEventListener('dragstart', (event) => event.preventDefault());
  }

  /* ---------- position ---------- */

  indexOf(row) {
    return ((row.pos % row.count) + row.count) % row.count;
  }

  get index() {
    return this.indexOf(this.clock);
  }

  step(delta, restartClock = false) {
    // Moving on (swipe, dot, arrow key) leaves the open tile behind.
    if (delta) this.closeTiles();
    if (!delta) {
      if (restartClock) this.restartClock();
      return;
    }

    this.rows.forEach((row) => {
      if (row.fixed) return;
      // Always set off from inside the middle copy. If the previous move's
      // `transitionend` has not landed yet — a fast run of dot clicks, or
      // a visitor with reduced motion, where it never fires at all — this
      // is what stops the position walking off the end of the cloned
      // track. The snap is invisible: the same tile stays under centre.
      this.normalise(row);
      row.pos += delta;
      this.write(row);
      this.scheduleNormalise(row);
    });

    this.renderDots(restartClock);
  }

  // Dots address a story, not a position: travel whichever way round is
  // shorter, so clicking the last dot moves one step back, not five
  // forward. Every row moves by that same number of its own tiles.
  goToIndex(target) {
    let delta = target - this.index;
    if (delta > this.clock.count / 2) delta -= this.clock.count;
    if (delta < -this.clock.count / 2) delta += this.clock.count;
    this.step(delta, true);
  }

  // `transitionend` is the normal trigger, but it never fires for a
  // visitor with reduced motion (the transition is removed) or for a move
  // the browser skips in a background tab. normalise() is a no-op when the
  // position is already inside the middle copy, so the two paths cannot
  // fight.
  scheduleNormalise(row) {
    clearTimeout(row.normaliseTimer);
    row.normaliseTimer = setTimeout(() => this.normalise(row), 900);
  }

  normalise(row) {
    if (row.fixed) return;
    if (row.pos >= row.count * 2 || row.pos < row.count) {
      const wrapped = row.count + this.indexOf(row);
      if (wrapped === row.pos) return;
      row.track.classList.add('is-jumping');
      row.pos = wrapped;
      this.write(row);
      void row.track.offsetWidth; // commit the untransitioned position
      row.track.classList.remove('is-jumping');
    }
  }

  // Fill without cropping: every tile keeps its row's height and takes its
  // width from its own image's aspect ratio, so the art fits edge to edge.
  // The row height is measured with the fitted overrides OFF (the stylesheet
  // decides it, from width or from the full-screen slot), then locked in px.
  fit() {
    const phone = window.matchMedia('(max-width: 749px)').matches;
    this.root.classList.remove('is-fitted');
    // Phones keep a clear peek of the neighbours on both sides.
    const maxShare = phone ? 0.86 : 0.92;

    const sizes = this.rows.map((row) => {
      const card = row.track.querySelector('.wt-newsletter__card');
      const height = card ? card.getBoundingClientRect().height : 0;
      return { height, maxW: row.el.clientWidth * maxShare };
    });

    // Not laid out (hidden tab/iframe, display:none): leave the stylesheet's
    // sizing in place. The resize listener fits it once it has a size.
    if (sizes.some((size) => !size.height)) {
      this.rows.forEach((row) => this.write(row));
      return;
    }

    this.rows.forEach((row, i) => {
      const { height, maxW } = sizes[i];
      row.el.style.setProperty('--wt-nl-row-h', `${height}px`);
      Array.from(row.track.children).forEach((slide) => {
        const ar = parseFloat(phone ? slide.dataset.arMobile : slide.dataset.ar) || 16 / 9;
        // A very wide image would overflow the row at full height; it gets
        // the max width. Desktop gives it a shorter card, still uncropped;
        // phones keep the full row height and let object-fit trim the sides,
        // so the taller phone rows never show short tiles with empty bands.
        const width = Math.min(height * ar, maxW);
        slide.style.setProperty('--wt-nl-tile-w', `${width}px`);
        slide.style.setProperty('--wt-nl-tile-h', `${phone ? height : width / ar}px`);
      });
    });

    this.root.classList.add('is-fitted');
    this.rows.forEach((row) => {
      row.track.classList.add('is-jumping');
      this.write(row);
      void row.track.offsetWidth;
      row.track.classList.remove('is-jumping');
    });
  }

  write(row) {
    row.track.style.setProperty('--wt-nl-pos', row.pos);

    const tile = row.track.children[row.pos];
    if (tile) {
      // Rect difference, not offsetLeft: the track's will-change can make it
      // the offsetParent in some engines. Both rects share the same
      // translation, so the difference is layout-only.
      const t = tile.getBoundingClientRect();
      const offset = t.left - row.track.getBoundingClientRect().left + t.width / 2;
      row.track.style.setProperty('--wt-nl-offset', `${offset}px`);
    }

    const current = row.track.querySelector('.is-active');
    if (current) current.classList.remove('is-active');
    const next = row.track.children[row.pos];
    if (next) next.classList.add('is-active');
  }

  renderDots(restartClock) {
    const index = this.index;
    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (restartClock) this.restartClock();
  }

  // A dot that is already active keeps its half-finished fill after a
  // manual move; restart it so the visitor gets a whole interval.
  restartClock() {
    const fill = this.dots[this.index] && this.dots[this.index].querySelector('.wt-newsletter__dot-fill');
    if (!fill) return;
    fill.style.animation = 'none';
    void fill.offsetWidth;
    fill.style.animation = '';
  }

  /* ---------- play / pause ---------- */

  applyPaused() {
    // A tile must not slide away while its caption is being read, so the
    // open dialog pauses the clock exactly as an open phone panel does.
    const paused =
      this.userPaused || !this.inView || document.hidden || !!this.tileOpen || !!this.dialogOpen;
    this.root.classList.toggle('is-paused', paused);

    if (this.toggle) {
      // aria-pressed tracks the button's own meaning ("pause is engaged"),
      // so it follows the visitor's choice rather than the automatic
      // out-of-view and background-tab pauses.
      this.toggle.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      this.toggle.setAttribute(
        'aria-label',
        this.userPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause
      );
    }
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.onFitResize) window.removeEventListener('resize', this.onFitResize);
    if (this.onCaptionResize) window.removeEventListener('resize', this.onCaptionResize);
    cancelAnimationFrame(this.fitFrame);
    cancelAnimationFrame(this.captionFrame);
    if (this.dialog && this.dialog.open) this.dialog.close();
    this.rows.forEach((row) => clearTimeout(row.normaliseTimer));
    clearTimeout(this.resizeTimer);
  }
}

(() => {
  const instances = new WeakMap();

  const init = (scope = document) => {
    scope.querySelectorAll('[data-newsletter-carousel]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new NewsletterCarousel(root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-newsletter-carousel]').forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.destroy();
      instances.delete(root);
    });
  });

  // Selecting a block in the editor should show that tile and hold on it.
  // Only feature tiles map to a dot; selecting a small tile just pauses,
  // since its row has its own length and no dot of its own.
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-newsletter-carousel]');
    const instance = root && instances.get(root);
    if (!instance || !instance.dots || !instance.dots.length) return;
    const index = parseInt(event.target.dataset.blockIndex, 10);
    if (!Number.isNaN(index) && event.target.dataset.blockRole === 'feature') {
      instance.goToIndex(index);
    }
    instance.userPaused = true;
    instance.applyPaused();
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest('[data-newsletter-carousel]');
    const instance = root && instances.get(root);
    if (!instance || !instance.dots || !instance.dots.length) return;
    instance.userPaused = root.dataset.autoplay === 'false';
    instance.applyPaused();
  });
})();
